import type { ReactNode } from "react";
import clsx from "clsx";

/** Flat card (e0: border only). Title is an h2 at the h3 scale; actions sit right. */
export function Card({ title, actions, children, className, padded = true, as: Tag = "section" }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padded?: boolean; as?: "section" | "div" | "article" }) {
  return (
    <Tag className={clsx("iga-card", className)}>
      {(title || actions) && (
        <header className="flex min-h-12 items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <h2 className="text-h3">{title}</h2>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? "p-4" : ""}>{children}</div>
    </Tag>
  );
}

/** Count tile. Tone colours the number only; `good` is accepted as an alias of `ok`. */
export function Stat({ label, value, hint, tone = "default" }: { label: string; value: ReactNode; hint?: string; tone?: "default" | "good" | "ok" | "warn" | "bad" }) {
  const tones = { default: "text-fg", good: "text-ok", ok: "text-ok", warn: "text-warn", bad: "text-bad" };
  return (
    <div className="iga-card p-4">
      <div className="text-label text-fg-3">{label}</div>
      <div className={clsx("mt-1 text-2xl font-bold tracking-[-0.01em]", tones[tone])}>{value}</div>
      {hint && <div className="mt-1 text-small text-fg-3">{hint}</div>}
    </div>
  );
}

/** Label / value grid. Empty values show "–" (§7). */
export function KeyValue({ items, columns = 2 }: { items: { label: string; value: ReactNode }[]; columns?: 1 | 2 }) {
  return (
    <dl className={clsx("grid grid-cols-1 gap-x-6 gap-y-2.5", columns === 2 && "sm:grid-cols-2")}>
      {items.map((i) => (
        <div key={i.label} className="flex flex-col">
          <dt className="text-small text-fg-3">{i.label}</dt>
          <dd className="font-medium">{i.value === undefined || i.value === null || i.value === "" ? <span className="text-fg-3">–</span> : i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Two-column facts list (label left, value right) as used in side panels and inbox details. */
export function Facts({ items, labelWidth = "120px", className }: { items: { label: string; value: ReactNode }[]; labelWidth?: string; className?: string }) {
  return (
    <dl className={clsx("grid gap-x-3 gap-y-2", className)} style={{ gridTemplateColumns: `${labelWidth} minmax(0, 1fr)` }}>
      {items.map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-fg-3">{i.label}</dt>
          <dd className="min-w-0">{i.value === undefined || i.value === null || i.value === "" ? <span className="text-fg-3">–</span> : i.value}</dd>
        </div>
      ))}
    </dl>
  );
}
